import { PropsWithChildren, useRef } from 'react'

import { useClickAway } from 'ahooks'
import { Dropdown, type MenuProps } from 'antd'
import { createStyles } from 'antd-style'

import { useMenuItems } from '@eastcoal/common/hooks/useMenuItems'

import { useContextMenuStore } from '../Store/hooks/useStore'

import { ContextMenuCoreProps } from './interface'

const useStyles = createStyles(({ prefixCls, token }) => ({
  menu: {
    [`&.${prefixCls}-dropdown-menu`]: {
      [`.${prefixCls}-dropdown-menu-item`]: {
        minWidth: '120px',
        padding: `${token.paddingXS}px ${token.paddingMD}px`,
        userSelect: 'none'
      },
      [`.${prefixCls}-dropdown-menu-item-disabled`]: {
        color: token.colorTextDisabled,
        [`&:hover`]: {
          color: token.colorTextDisabled
        }
      }
    }
  },
  trigger: {
    position: 'fixed',
    width: `1px`,
    height: `1px`,
    visibility: 'hidden'
  }
}))

export const ContextMenuCore = ({
  children,
  items: externalItems,
  getPopupContainer,
  onClick
}: PropsWithChildren<ContextMenuCoreProps>) => {
  const { visible, style, close } = useContextMenuStore(state => state)

  const { styles } = useStyles()

  const containerRef = useRef<HTMLElement>(null)

  const childrenContainer = useRef<HTMLDivElement>(null)
  useClickAway(close, [childrenContainer], ['click', 'contextmenu'])

  const { items, isEmpty } = useMenuItems({ items: externalItems })

  const handleMenuClick: MenuProps['onClick'] = info => {
    onClick?.(info)
    close()
  }

  return isEmpty ? null : (
    <>
      <div ref={childrenContainer}>{children}</div>
      <Dropdown
        key={`${style?.top}-${style?.left}`}
        autoAdjustOverflow
        destroyOnHidden={false}
        forceRender={false}
        open={visible}
        getPopupContainer={dom => {
          containerRef.current = getPopupContainer
            ? getPopupContainer(dom)
            : document.body
          return containerRef.current
        }}
        menu={{
          items,
          rootClassName: styles.menu,
          onClick: handleMenuClick
        }}
      >
        <span className={styles.trigger} style={style} />
      </Dropdown>
    </>
  )
}
